/**
 * Design System - Main Theme Configuration
 * Configurazione centralizzata del tema Material-UI
 */

import { createTheme, ThemeOptions } from '@mui/material/styles';
import { palette } from './tokens/colors';
import { typographyVariants } from './tokens/typography';
import { spacing, createSpacing, spacingValues } from './tokens/spacing';
import { breakpointValues } from './tokens/breakpoints';
import { shadowArray } from './tokens/shadows';

// Component overrides per standardizzare l'aspetto
const componentOverrides: ThemeOptions['components'] = {
  // Button overrides
  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 500,
        borderRadius: spacingValues.borderRadius.md,
        padding: `${spacing.sm}px ${spacing.md}px`,
        boxShadow: 'none',
        '&:hover': {
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
        },
        '&:active': {
          boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.2)',
        },
      },
      contained: {
        '&:hover': {
          boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
        },
      },
      outlined: {
        borderWidth: '1px',
        '&:hover': {
          borderWidth: '1px',
        },
      },
    },
    variants: [
      {
        props: { variant: 'contained', size: 'small' },
        style: {
          padding: `${spacing.xs}px ${spacing.sm}px`,
          fontSize: '0.875rem',
        },
      },
      {
        props: { variant: 'contained', size: 'large' },
        style: {
          padding: `${spacing.md}px ${spacing.lg}px`,
          fontSize: '1rem',
        },
      },
    ],
  },

  // TextField overrides
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: spacingValues.borderRadius.md,
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: palette.primary.main,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: '2px',
          },
        },
      },
    },
  },

  // Paper overrides
  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: spacingValues.borderRadius.lg,
        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
      },
      elevation1: {
        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
      },
      elevation2: {
        boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.15)',
      },
      elevation3: {
        boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.2)',
      },
    },
  },

  // Table overrides
  MuiTable: {
    styleOverrides: {
      root: {
        '& .MuiTableHead-root': {
          '& .MuiTableCell-head': {
            backgroundColor: palette.grey[50],
            fontWeight: 600,
            fontSize: '0.875rem',
            color: palette.text.primary,
            borderBottom: `2px solid ${palette.divider}`,
          },
        },
        '& .MuiTableBody-root': {
          '& .MuiTableRow-root': {
            '&:hover': {
              backgroundColor: palette.grey[50],
            },
            '&:nth-of-type(even)': {
              backgroundColor: palette.grey[25],
            },
          },
        },
      },
    },
  },

  // TableCell overrides
  MuiTableCell: {
    styleOverrides: {
      root: {
        borderBottom: `1px solid ${palette.divider}`,
        padding: `${spacing.sm}px ${spacing.md}px`,
      },
      head: {
        fontWeight: 600,
        fontSize: '0.875rem',
      },
    },
  },

  // Chip overrides
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: spacingValues.borderRadius.sm,
        fontWeight: 500,
        fontSize: '0.75rem',
      },
      outlined: {
        borderWidth: '1px',
      },
    },
  },

  // Dialog overrides
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: spacingValues.borderRadius.xl,
        boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.2)',
      },
    },
  },

  // DialogTitle overrides
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        padding: `${spacing.lg}px ${spacing.lg}px ${spacing.md}px`,
        borderBottom: `1px solid ${palette.divider}`,
        fontWeight: 600,
      },
    },
  },

  // DialogContent overrides
  MuiDialogContent: {
    styleOverrides: {
      root: {
        padding: `${spacing.lg}px`,
      },
    },
  },

  // DialogActions overrides
  MuiDialogActions: {
    styleOverrides: {
      root: {
        padding: `${spacing.md}px ${spacing.lg}px ${spacing.lg}px`,
        borderTop: `1px solid ${palette.divider}`,
        gap: spacing.sm,
      },
    },
  },

  // FormControl overrides
  MuiFormControl: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: spacingValues.borderRadius.md,
        },
      },
    },
  },

  // Select overrides
  MuiSelect: {
    styleOverrides: {
      root: {
        borderRadius: spacingValues.borderRadius.md,
      },
    },
  },

  // MenuItem overrides
  MuiMenuItem: {
    styleOverrides: {
      root: {
        padding: `${spacing.sm}px ${spacing.md}px`,
        '&:hover': {
          backgroundColor: palette.grey[50],
        },
        '&.Mui-selected': {
          backgroundColor: palette.primary[50],
          '&:hover': {
            backgroundColor: palette.primary[100],
          },
        },
      },
    },
  },

  // IconButton overrides
  MuiIconButton: {
    styleOverrides: {
      root: {
        borderRadius: spacingValues.borderRadius.sm,
        '&:hover': {
          backgroundColor: palette.grey[100],
        },
      },
    },
  },

  // Typography overrides
  MuiTypography: {
    styleOverrides: {
      h1: typographyVariants.h1,
      h2: typographyVariants.h2,
      h3: typographyVariants.h3,
      h4: typographyVariants.h4,
      h5: typographyVariants.h5,
      h6: typographyVariants.h6,
      subtitle1: typographyVariants.subtitle1,
      subtitle2: typographyVariants.subtitle2,
      body1: typographyVariants.body1,
      body2: typographyVariants.body2,
      button: typographyVariants.button,
      caption: typographyVariants.caption,
      overline: typographyVariants.overline,
    },
  },
};

// Configurazione del tema principale
export const theme = createTheme({
  palette,
  typography: typographyVariants,
  spacing: createSpacing(spacing.unit),
  breakpoints: {
    values: breakpointValues,
  },
  shadows: [...shadowArray] as any,
  shape: {
    borderRadius: 8, // 8px - valore fisso per il border radius
  },
  components: componentOverrides,
});

// Tema per modalità scura (futuro)
export const darkTheme = createTheme({
  ...theme,
  palette: {
    ...theme.palette,
    mode: 'dark',
    background: {
      default: '#121212',
      paper: '#1e1e1e',
    },
    text: {
      primary: '#ffffff',
      secondary: 'rgba(255, 255, 255, 0.7)',
      disabled: 'rgba(255, 255, 255, 0.5)',
    },
  },
});

export default theme;
